import { z } from "zod";
/**
 * The feature flag contract (spec §18) — the typed key union and the initial
 * flag set from §18.5.
 *
 * §18 scopes this deliberately small: **a boolean per flag, global, is the
 * entire requirement.** There is one primary user. No percentage rollouts, no
 * user targeting, no A/B testing, no LaunchDarkly / Flagsmith / Unleash. Do not
 * grow this file into a targeting engine — there is nobody to target.
 *
 * The key union lives here, next to capabilities, so `useFlag("challan")` fails
 * to compile when the key is `challans` (§18.1). Never accept arbitrary strings.
 *
 * ORDERING (§18.2): `requireFlag` runs BEFORE `requireCap`.
 *   flag off      → 404 Not Found   — the feature does not exist here
 *   capability no → 403 Forbidden   — it exists, but not for you
 * A disabled feature must never return 403: "forbidden" leaks that unreleased
 * work exists and invites the owner to ask why he is denied something he sees.
 *
 * NEVER FLAGGABLE (§18.3) — flags gate features, not correctness. No flag, in
 * either state, may change: the GST tax engine or its rounding (§5); document
 * numbering or its atomicity (§6); the `audit_log` write on every mutation
 * (§13); authentication or the permission checks themselves (§2.1); required
 * GST invoice fields (§9.2) or HSN/SAC presence on a line (§4.1); server-side
 * recomputation of totals (§7). A flag that can switch off tax math or let
 * numbering skip is a compliance defect, not a feature toggle. This is also
 * what makes "unknown flag resolves to off" safe offline (§18.1) — no core
 * path is gated, so a failure to fetch flags can never block invoicing.
 */
/* -------------------------------------------------------------------------- */
/* The key union (§18.5)                                                       */
/* -------------------------------------------------------------------------- */
/**
 * The flag keys of §18.5, in table order. Each mirrors a §17 roadmap item, which
 * is why the roadmap and this list stay in step.
 *
 * (The comment said "ten" until 2026-08-18, by which point there were thirteen.
 * A count in prose next to the list it counts is a fact that rots on the next
 * commit; the list is the authority and `flags.test.ts` retypes §18.5 to check
 * it.)
 */
export const FEATURE_FLAG_KEYS = [
    "quotations",
    "invoices",
    "challans",
    "credit_notes",
    "payments",
    "purchases",
    "approval_links",
    "accountant_role",
    "marathi_ui",
    "einvoice",
    "modern_pdf",
    "notifications",
    "messages",
    "mail",
    "printed_signature_invoice",
    "printed_signature_quotation",
    "printed_signature_challan",
    "printed_signature_credit_note",
];
/* -------------------------------------------------------------------------- */
/* The printed signature, per document kind (§9.2, §18.5)                      */
/* -------------------------------------------------------------------------- */
/**
 * The four documents that can carry the proprietor's scanned signature.
 *
 * Its own union rather than a reuse of a document-kind type from elsewhere: the
 * set that can be SIGNED is not by definition the set that can be issued, and
 * tying the two would mean a new document kind silently acquiring a signature
 * flag it was never designed a space for.
 */
export const SIGNABLE_DOCUMENT_KINDS = [
    "invoice",
    "quotation",
    "challan",
    "creditNote",
];
/**
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SIGNATURE FLAG PER DOCUMENT KIND (2026-08-20) — WAS ONE FLAG OVER ALL
 * FOUR, AND THE OWNER ASKED FOR THEM SPLIT.
 *
 * WHY, IN HIS TERMS: he wants the scan on a QUOTATION and a pen on the BILL, or
 * the reverse, and the single flag made that one decision for all four. A
 * quotation is a commercial offer he is happy to sign in advance; a tax invoice
 * is the document he has been signing by hand and posting since he lost the DSC
 * token, and whether a picture of his pen belongs on it is a different question
 * with a different answer.
 *
 * FOUR FLAGS RATHER THAN A SETTING, AND THIS WAS A REAL CHOICE. A
 * `company.signature_on_*` column quartet would not have cost the §18.4 budget
 * anything, and a per-kind SETTING is arguably what this is. It stays a FLAG
 * because everything about how it behaves is a flag: it is global (one company,
 * §4), it is operator state an ADMIN throws and a deploy must never reset, its
 * blast radius wants the "here is what stops working" confirmation the flags
 * screen already draws, and — the deciding one — turning it off must be
 * possible WITHOUT a schema change on a box mid-month. Moving it to a column
 * would also mean writing a fifth reader for a question four call sites already
 * answer identically. See flags.test.ts for the cap argument that comes with
 * this.
 *
 * §18.3 IS UNCHANGED AND IS STILL THE ONLY QUESTION WORTH ARGUING. Splitting
 * one presentation flag into four presentation flags cannot make it a
 * correctness flag: each of the four switches whether a MARK is drawn on ITS
 * OWN sheet, and with any of them off that sheet carries the same blank space
 * for a pen, under the same `For <trade name>` / `Authorised Signatory.`
 * caption, that it has carried since §9.2 was written. Every §9.2 field, every
 * figure and every total is identical either way, on both templates of all four
 * documents. Rule 46(q) asks for "the signature or digital signature of the
 * supplier or his authorised representative" — signing the printout by hand is
 * how this business satisfies it today.
 *
 * AND NONE OF THEM IS A DIGITAL SIGNATURE. It is a picture in a PDF: no key, no
 * certificate, nothing tamper-evident. The ADMIN copy says so in those words on
 * every one of the four rows, because a flag called "signature" that a reader
 * mistakes for a DSC would be worse than no flag.
 *
 * THEY ARE READ, NEVER `requireFlag` (§18.2). A `GET /invoices/:id/pdf` that
 * answered 404 because a signature was switched off would be a bug, not a gate.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * The one place the kind → key mapping exists. `printed-signature.ts` in the
 * API and the ADMIN screen both read it, so there is no second table to drift.
 */
export const PRINTED_SIGNATURE_FLAGS = Object.freeze({
    invoice: "printed_signature_invoice",
    quotation: "printed_signature_quotation",
    challan: "printed_signature_challan",
    creditNote: "printed_signature_credit_note",
});
/**
 * §18.4's SOFT cap on LIVE NON-PERMANENT flags — "more than about ten and flags
 * are standing in for finishing things".
 *
 * SOFT, AND NOTHING ENFORCES IT. Nothing may refuse a toggle for being the
 * eleventh: the day that mattered would be the day a broken module had to go
 * dark, and a limit that blocks the kill switch is worse than the sprawl it was
 * guarding against. It is a number the ADMIN screen counts against and says out
 * loud, which is the whole of its job.
 *
 * It lives here rather than in the route or the screen because BOTH read it —
 * `GET /flags` answers `liveNonPermanent` and the screen compares against this —
 * and a cap spelled twice is a cap that disagrees with itself.
 */
export const FEATURE_FLAG_LIVE_SOFT_CAP = 10;
/** Runtime validation for a flag key arriving as untrusted data (route params, cache). */
export const FeatureFlagKeySchema = z.enum(FEATURE_FLAG_KEYS);
/** Narrows an arbitrary runtime value to a {@link FeatureFlagKey}. */
export function isFeatureFlagKey(value) {
    return typeof value === "string" && FEATURE_FLAG_KEYS.includes(value);
}
/**
 * The §18.5 initial flag set, transcribed. Consumed by the seed and by the flag
 * service when a key has no row yet.
 *
 * `quotations` and `invoices` are the only two that start ON, and they are
 * ROLLOUT TOOLS, NOT PERMANENT ARCHITECTURE (§18.5): they exist only so the two
 * core modules can be handed to the owner one at a time during Phase 4. Both
 * flags — and their dead branches — come out once he is billing daily. Do not
 * build anything that assumes they will always be there, and do not let them
 * become the switch that "turns off invoicing".
 *
 * Deliberately NOT in this table: `owner` and `reviewBy`. §18.4 requires both on
 * every flag row, but §18.5 does not specify values for them, and they are
 * people-and-calendar data that would go stale in source. They are supplied at
 * seed/creation time and stored on `feature_flags`; this table is only the
 * spec-fixed part (§18.5). A flag row created without an owner and a review date
 * violates §18.4 regardless of what this file says.
 */
export const FEATURE_FLAG_DEFAULTS = Object.freeze({
    quotations: Object.freeze({
        key: "quotations",
        enabled: true,
        description: "Quotation module (§7)",
        isPermanent: false,
        removalNote: "Remove after v1 stabilises — a rollout tool, not architecture.",
    }),
    invoices: Object.freeze({
        key: "invoices",
        enabled: true,
        description: "Invoice module (§7)",
        isPermanent: false,
        removalNote: "Remove after v1 stabilises — a rollout tool, not architecture.",
    }),
    /*
     * ON SINCE 2026-08-16. It shipped off while §17.7 was built, and the last
     * thing holding it was the owner reviewing the printed challan — because a
     * JOB_WORK_IN challan names the CUSTOMER as the consigner, and that is the one
     * field on the document that is legally false rather than untidy if inverted.
     * He read the rendered samples and approved them, so the gate is cleared.
     *
     * The flag stays declared rather than deleted for now: §18.3 says to remove a
     * flag AND its dead branch in the same commit once the feature is stable, and
     * "stable" means after it has carried real goods, not after it compiles.
     */
    challans: Object.freeze({
        key: "challans",
        enabled: true,
        description: "Delivery challans (§17.7)",
        isPermanent: false,
        removalNote: "Remove once challans have carried real goods for a few weeks.",
    }),
    credit_notes: Object.freeze({
        key: "credit_notes",
        enabled: false,
        description: "Credit notes (§17.3)",
        isPermanent: false,
        removalNote: "Remove once credit notes ship.",
    }),
    payments: Object.freeze({
        key: "payments",
        enabled: false,
        description: "Payment tracking (§17.5)",
        isPermanent: false,
        removalNote: "Remove once payment tracking ships.",
    }),
    purchases: Object.freeze({
        key: "purchases",
        enabled: false,
        description: "Purchase / ITC entry (§17.2)",
        isPermanent: false,
        removalNote: "Remove once purchase entry ships.",
    }),
    approval_links: Object.freeze({
        key: "approval_links",
        enabled: false,
        description: "Customer magic links (§17.1)",
        isPermanent: false,
        removalNote: "Remove once approval links ship.",
    }),
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * THE CA'S LOGIN — BUILT 2026-08-17, AND STILL OFF.
     *
     * The owner decided it ("CA WILL have their login and their view"), so this
     * stopped being "an unbuilt feature" and became "a built one the owner has not
     * switched on". The distinction is the whole reason it ships off: turning it
     * on grants an EXTERNAL PROFESSIONAL her own credentials on a system holding
     * the GSTIN, the PAN, the bank details and every invoice the business has
     * issued, and that is his decision to make on a day he chooses, not a deploy's
     * side effect.
     *
     * WHAT IT GATES, EXACTLY: whether a row with `role = 'ACCOUNTANT'` may hold a
     * session — checked at login, at refresh, and on every authenticated request
     * (`ROLE_FEATURE_FLAGS` in permissions.ts, `auth/roles.ts` in the API). It
     * gates NOTHING ELSE. It changes no capability, no route, no tax figure and no
     * audit write, and it can only ever DENY: there is no state of it that lets
     * somebody in who could not otherwise get in. That is the §18.3 line, and
     * permissions.ts argues it at length next to the map.
     *
     * A STORED ROW BEATS THIS DEFAULT. Flipping `enabled` here changes nothing on
     * a database that has been seeded — `02_seed.sql` writes the row — so turning
     * her login on is the ADMIN flags screen (or one UPDATE), never a deploy.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    accountant_role: Object.freeze({
        key: "accountant_role",
        enabled: false,
        description: "ACCOUNTANT role — whether the CA may hold a session (§2, §2.1)",
        isPermanent: false,
        removalNote: "Remove once the CA has been signing in for a few weeks — delete the flag AND its entry in ROLE_FEATURE_FLAGS in the same commit (§18.4).",
    }),
    marathi_ui: Object.freeze({
        key: "marathi_ui",
        enabled: false,
        description: "Marathi locale toggle (§11.4)",
        isPermanent: false,
        removalNote: "Enable once strings are reviewed by a native speaker, then remove.",
    }),
    einvoice: Object.freeze({
        key: "einvoice",
        enabled: false,
        description: "IRN/QR via GSP (§10)",
        isPermanent: true,
        removalNote: "Permanent — a turnover-threshold switch, not temporary.",
    }),
    /*
     * §9's 2026-08-14 amendment. Switches the PRINTED LOOK of the INVOICE and the
     * QUOTATION between the replicated originals and the dense, boxed work-order
     * layout set in JetBrains Mono.
     *
     * ONE KEY FOR BOTH DOCUMENTS, DELIBERATELY. The owner is switching "the new
     * look" on, not picking a layout per document kind, and a business that sent a
     * modern quotation and then a classic invoice for the same job would look like
     * two businesses. Both routes read it per request, and neither uses
     * `requireFlag` — it chooses a layout, it does not gate a route, and a
     * document PDF must always be printable whichever way it is set.
     *
     * THIS DOES NOT GATE CORRECTNESS, and the distinction is the reason it is
     * allowed to exist at all. §18.3 forbids a flag that changes "required GST
     * invoice fields (§9.2)". Both invoice templates carry EVERY one of those
     * fields, and both quotation templates carry every §9.3 field — the flag
     * decides where they sit on the page and what they are set in, nothing more.
     * `pdf-modern-invoice.test.ts` and `pdf-modern-quotation.test.ts` assert each
     * list is identical in both states; if either ever has to be relaxed, this
     * flag has become a compliance defect and must be deleted rather than fixed.
     *
     * It starts OFF because the existing layouts are the ones Lupin's AP team
     * already accepts (§9), and switching what a paying customer receives is the
     * owner's call to make deliberately, not a deploy's side effect.
     *
     * (The description read "Modern serif invoice PDF template" until 2026-08-15
     * and was stale twice over: the face is monospace, not serif, and the flag now
     * covers two document kinds.)
     */
    modern_pdf: Object.freeze({
        key: "modern_pdf",
        enabled: false,
        description: "Modern work-order PDF layout for invoices and quotations (§9 amendment)",
        isPermanent: false,
        removalNote: "Remove once the owner has settled on one look — delete the flag AND the losing templates for BOTH documents in the same commit (§18.3).",
    }),
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * THE NOTIFICATION HUB (2026-08-17) — the in-app inbox and the events that
     * fill it, `/api/v1/notifications` and every `notify()` write.
     *
     * WHAT IT DOES **NOT** GATE, and this is the §18.3 line: it does not gate
     * RECORDING AN E-WAY BILL NUMBER. Those routes ride the module flag of the
     * document they hang off — `requireFlag("invoices")` and
     * `requireFlag("challans")` — exactly as `POST /invoices/:id/einvoice` already
     * does. Two flags over one route would mean a bill that can be issued but
     * whose government-issued number cannot be filed against it, which is a state
     * with no owner and no reason to exist.
     *
     * SO A DISABLED HUB LOSES ONLY THE TELLING. Invoices still save, the e-way
     * bill number still records, the audit row is still written; nobody is
     * notified. That is precisely the kill switch §18.1 asks for — if the hub ever
     * misbehaves, switching it off must never be able to stop the owner billing.
     * Nothing behind this flag touches tax math, numbering, the audit write,
     * authentication or the recomputation of totals (§18.3).
     *
     * IT SHIPS OFF, like every other module on its first day. The owner turns it
     * on when he has seen the bell do something he wants. One consequence worth
     * knowing before that day: while it is off NO ROWS ARE WRITTEN AT ALL, so
     * switching it on starts the inbox from that moment — bills issued before it
     * carry no "needs an e-way bill" row, and back-filling one is not something
     * this feature does.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    notifications: Object.freeze({
        key: "notifications",
        enabled: false,
        description: "In-app notification hub and its events, incl. the e-way bill threshold (§17.7)",
        isPermanent: false,
        removalNote: "Remove once the owner has run with the bell on for a few weeks — it is a rollout tool, not a kill switch for compliance alerts.",
    }),
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * THE IN-APP MESSENGER — `/api/v1/messages` AND `/api/v1/messages/direct`,
     * AND THE ONE SCREEN THAT LISTS BOTH.
     *
     * IT GATES THE MESSENGER ENTIRE — the group chat AND the private
     * conversations, which are two kinds of row on one list at `/messages` since
     * 2026-08-19. `message.view` and `message.direct` decide which rows a given
     * person sees; this decides whether any of it exists on this server. Both
     * halves therefore go dark together, deliberately: they are one feature, and a
     * flag that took only one of them would leave a reader with half a messenger
     * and no way to tell which half.
     *
     * WHAT IT GATES IS THAT FEATURE AND NOTHING ELSE. There is no emitter hanging
     * off an invoice write, no column on a document, and nothing in the tax
     * engine, the numbering, the audit write, authentication or the recomputation
     * of totals goes anywhere near it (§18.3). Switching it off removes one screen
     * and nine routes; every bill in the system is issued, numbered, taxed and
     * printed exactly as before. That is the cleanest kind of flag there is, and
     * it is why this one is allowed to exist at all.
     *
     * IT SHIPS OFF, like every other module on its first day. While it is off the
     * routes answer 404 (§18.2), the header control renders nothing, and NO ROWS
     * ARE WRITTEN — so switching it on starts the conversation from that moment.
     * There is nothing to back-fill and nothing that was lost.
     *
     * ONE CONSEQUENCE WORTH KNOWING BEFORE IT IS SWITCHED ON: the third
     * participant needs TWO flags. Her login was built on 2026-08-17 but
     * `accountant_role` (above) ships off, so `messages` on its own gives the
     * owner and his son a working conversation and leaves her out of it. Both
     * have to be on before the room has three people in it, and turning this one
     * on alone is not a bug — it is half the feature.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    messages: Object.freeze({
        key: "messages",
        enabled: false,
        description: "In-app messenger — private conversations and the shared group chat, on one list",
        isPermanent: false,
        removalNote: "Remove once the conversation has carried real traffic for a few weeks — a rollout tool, not architecture.",
    }),
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * EMAIL, INSIDE THE APP (§17A) — TRANSCRIBED FROM §18.5 ON 2026-08-18, WHERE
     * IT HAD BEEN SITTING SINCE THE SPEC WAS WRITTEN.
     *
     * This row is not a new flag somebody invented for a new feature: §18.5 lists
     * `mail` in its table, `prisma/sql/03_mail_tables.sql` names it in its header,
     * and §17A.4 says "the whole feature sits behind the `mail` flag". What was
     * missing was the transcription into this file, which is why nothing could be
     * gated on it until now.
     *
     * WHAT IT GATES TODAY: sending a document by email — `POST
     * /quotations/:id/email` and nothing else (§17A.2 stage 1). Receiving,
     * folders, drafts and search are stages 2 and 3 and do not exist.
     *
     * WHAT IT MAY NEVER GATE (§18.3). Nothing behind it touches the tax engine,
     * numbering, the audit write, authentication or the recomputation of totals.
     * Switching it off removes a menu item and one route; every bill and every
     * quotation is still raised, numbered, taxed, printed and shared on WhatsApp
     * exactly as before. That is what makes it a legitimate kill switch: §17A.3's
     * failure mode — mail silently filtered by a corporate spam system while the
     * owner believes the quotation went out — is strictly worse than having no
     * email feature, so being able to switch it off in one toggle is the point.
     *
     * IT SHIPS OFF, AND §18.5 IS UNUSUALLY SPECIFIC ABOUT WHEN IT MAY GO ON:
     * "enable once SPF, DKIM and DMARC pass an external check and stage 1 has sent
     * to a real customer". That is a DNS-and-deliverability gate, not a code gate,
     * and no amount of passing tests here clears it.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    mail: Object.freeze({
        key: "mail",
        enabled: false,
        description: "In-app email — sending a document with its PDF attached (§17A)",
        isPermanent: false,
        removalNote: "Enable only once SPF/DKIM/DMARC pass an external check (§17A.3) and stage 1 has reached a real customer; remove the flag once mail is routine.",
    }),
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * THE SCANNED SIGNATURE ON THE PRINTED SHEET — FOUR FLAGS SINCE 2026-08-20,
     * ONE SINCE 2026-08-18. The full argument for the split, and for these being
     * flags rather than a setting, is on {@link PRINTED_SIGNATURE_FLAGS} above.
     *
     * WHY THE FEATURE EXISTS AT ALL. The owner lost his DSC USB token, so every
     * invoice is now printed, signed with a pen and POSTED. A scan of that
     * signature drawn into the space the sheet already leaves for it is what lets
     * an emailed document replace the postage. It was built reading
     * `company.signature_url` alone — all or nothing — and he asked first for a
     * switch (so it could go dark WITHOUT clearing the column while he decided
     * whether he liked the image), and then for FOUR, so the answer can differ
     * between a quotation and a bill.
     *
     * ALL FOUR SHIP OFF, which is also what the single flag they replace was set
     * to — so no box changes behaviour when this lands. A stored row beats this
     * default and none of these keys is seeded (`02_seed.sql` carries the §18.5
     * table only), so on every existing database they resolve to OFF and the
     * sheets stay exactly as they print today.
     *
     * THE OLD KEY `printed_signature` IS GONE RATHER THAN ALIASED. It resolved to
     * off everywhere and was never switched on — release-two.md still lists it as
     * shipping off — so there is no operator state to carry across and an alias
     * would only be a fifth thing to read. A row left behind in `feature_flags`
     * shows on the ADMIN screen as "Leftover — no longer in the code", which is
     * exactly what it is and exactly what that badge is for.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    printed_signature_invoice: Object.freeze({
        key: "printed_signature_invoice",
        enabled: false,
        description: "Scanned signature printed on TAX INVOICES (§9.2) — a mark, not a field: with it off the sheet keeps the same blank signing space, and no §9.2 field changes either way",
        isPermanent: false,
        removalNote: "Remove once the owner has settled on how a BILL is signed — delete the flag AND its entry in PRINTED_SIGNATURE_FLAGS in the same commit (§18.4). Keep it while he is still comparing the printed mark with his pen.",
    }),
    printed_signature_quotation: Object.freeze({
        key: "printed_signature_quotation",
        enabled: false,
        description: "Scanned signature printed on QUOTATIONS (§9.3) — a mark, not a field: with it off the sheet keeps the same blank signing space",
        isPermanent: false,
        removalNote: "Remove once the owner has settled on how a QUOTATION is signed — delete the flag AND its entry in PRINTED_SIGNATURE_FLAGS in the same commit (§18.4).",
    }),
    printed_signature_challan: Object.freeze({
        key: "printed_signature_challan",
        enabled: false,
        description: "Scanned signature printed on DELIVERY CHALLANS (§17.7) — a mark, not a field: with it off the sheet keeps the same blank signing space",
        isPermanent: false,
        removalNote: "Remove once the owner has settled on how a CHALLAN is signed — delete the flag AND its entry in PRINTED_SIGNATURE_FLAGS in the same commit (§18.4).",
    }),
    printed_signature_credit_note: Object.freeze({
        key: "printed_signature_credit_note",
        enabled: false,
        description: "Scanned signature printed on CREDIT NOTES (§17.3) — a mark, not a field: with it off the sheet keeps the same blank signing space",
        isPermanent: false,
        removalNote: "Remove once the owner has settled on how a CREDIT NOTE is signed — delete the flag AND its entry in PRINTED_SIGNATURE_FLAGS in the same commit (§18.4).",
    }),
});
/**
 * Resolution for a key with no stored row (§18.1: "A flag that has never been
 * seen resolves to off"). Falls back to the §18.5 default rather than a blanket
 * `false` so a fresh database still shows the owner the two core modules; an
 * unknown key — one outside the union at runtime — is always `false`.
 */
export function defaultFlagEnabled(key) {
    return FEATURE_FLAG_DEFAULTS[key]?.enabled ?? false;
}
//# sourceMappingURL=flags.js.map